/**
 * CultivateSkillEffect — Fire tree Cultivate Fork: plant a fire zone.
 *
 * Instantaneously plants a 3-tile radius fire zone at the player's feet.
 * The zone deals burning DoT to all enemies inside (intensity 2, 1000ms tick).
 * The player is immune to their own zone via `friendlyEntityId`.
 * Maximum 2 zones active simultaneously — oldest is evicted before a third.
 *
 * **Architect** (`cultivate_passive`): multiplies zone duration × 1.3 at
 * creation time via `resolver.getTreePassiveEffect('fire', 'durationMultiplier')`.
 *
 * **Spread** (`cultivate_expand`): when `resolver.getAdditiveEffect('cultivate',
 * 'expansionTiles') > 0`, adds `expandsOverTime: true, expansionRateMs: 3000,
 * expansionTiles: 0.5, maxRadiusTiles: 5` to the zone config. Also enables
 * directional shove: if `deps.getShoveDirection()` returns a non-null vector,
 * `deps.zones.moveZone(zoneId, dx, dy, 1)` is called after zone creation.
 *
 * **Scorched Earth** (`cultivate_aftermath`): when `resolver.hasEffect('cultivate',
 * 'ashRadiusTiles')` is true, an `onExpiry` callback is injected at zone creation
 * time. On natural expiry the callback fires and creates an `ash_field` zone at
 * the same position (radius 2, duration 4000ms, speedMultiplier 0.8). Does NOT
 * fire on early removal (max-zone eviction).
 *
 * Long cooldown tier. Breaks Hide on activation.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see fire-spec.md § Cultivate, § Spread, § Scorched Earth, § Architect
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_ZONES = 2;

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface CultivateZoneConfig {
  readonly id: string;
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly shape: string;
  readonly radiusTiles: number;
  readonly durationMs: number;
  readonly dotDefId: string;
  readonly dotIntensity: number;
  readonly tickIntervalMs: number;
  readonly sourceSkillId: string;
  /** Entity immune to this zone's DoT (the player). */
  readonly friendlyEntityId?: string;
  // Expansion (Spread upgrade)
  readonly expandsOverTime?: boolean;
  readonly expansionRateMs?: number;
  readonly expansionTiles?: number;
  readonly maxRadiusTiles?: number;
  // Expiry callback (Scorched Earth upgrade)
  readonly onExpiry?: (zone: { config: CultivateZoneConfig }) => void;
}

export interface AshFieldZoneConfig {
  readonly id: string;
  readonly type: string;
  readonly position: { x: number; y: number };
  readonly shape: string;
  readonly radiusTiles: number;
  readonly durationMs: number;
  readonly speedMultiplier: number;
  readonly sourceSkillId: string;
}

export interface CultivateDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getTreePassiveEffect(treeId: string, key: string, aggregation: 'product'): number;
    hasEffect(skillId: string, key: string): boolean;
  };
  /** Returns the player's current world position. */
  readonly getPlayerPosition: () => { x: number; y: number };
  /** Returns the player's entity ID for friendly-fire exclusion. */
  readonly getPlayerEntityId: () => string;
  /**
   * Returns the current directional shove vector (Spread upgrade).
   * Non-null when the player held a direction during the Cultivate cast.
   * `{ dx, dy }` are normalized (-1, 0, or 1 per axis).
   * Returns null if no directional input is active.
   */
  readonly getShoveDirection: () => { dx: number; dy: number } | null;
  readonly breakHide: () => void;
  readonly zones: {
    create(config: CultivateZoneConfig): string;
    createAshField(config: AshFieldZoneConfig): string;
    remove(zoneId: string): void;
    moveZone(zoneId: string, dx: number, dy: number, tiles: number): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface CultivateConfig {
  readonly cooldownMs: number;
  readonly radiusTiles: number;
  readonly baseDurationMs: number;
  readonly dotIntensity: number;
  readonly tickIntervalMs: number;
}

const DEFAULT_CONFIG: CultivateConfig = {
  cooldownMs: 2000,
  radiusTiles: 3,
  baseDurationMs: 10000,
  dotIntensity: 2,
  tickIntervalMs: 1000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class CultivateSkillEffect implements SkillEffect {
  readonly skillId = 'cultivate';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: CultivateDeps;
  private readonly config: CultivateConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private readonly _activeZoneIds: string[] = [];

  constructor(deps: CultivateDeps, config: Partial<CultivateConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get activeZoneIds(): ReadonlyArray<string> { return this._activeZoneIds; }

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this.deps.breakHide();

    // Max-zone enforcement
    if (this._activeZoneIds.length >= MAX_ZONES) {
      const oldest = this._activeZoneIds.shift()!;
      this.deps.zones.remove(oldest);
    }

    // Architect duration multiplier
    const durationMultiplier = this.deps.resolver.getTreePassiveEffect('fire', 'durationMultiplier', 'product');
    const resolvedDuration = Math.round(this.config.baseDurationMs * durationMultiplier);

    // Spread: expansion config
    const expansionTiles = this.deps.resolver.getAdditiveEffect('cultivate', 'expansionTiles');
    const spreadActive = expansionTiles > 0;
    const expansionRateMs = spreadActive
      ? this.deps.resolver.getAdditiveEffect('cultivate', 'expansionRateMs')
      : 0;
    const maxRadiusTiles = spreadActive
      ? this.deps.resolver.getAdditiveEffect('cultivate', 'maxRadiusTiles')
      : 0;

    // Scorched Earth: onExpiry ash field callback
    const scorchedActive = this.deps.resolver.hasEffect('cultivate', 'ashRadiusTiles');
    const ashRadiusTiles = scorchedActive
      ? this.deps.resolver.getAdditiveEffect('cultivate', 'ashRadiusTiles')
      : 0;
    const ashDurationMs = scorchedActive
      ? this.deps.resolver.getAdditiveEffect('cultivate', 'ashDurationMs')
      : 0;

    const onExpiry = scorchedActive
      ? (zone: { config: CultivateZoneConfig }) => {
          this.deps.zones.createAshField({
            id: `ash-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            type: 'ash_field',
            position: zone.config.position,
            shape: 'circle',
            radiusTiles: ashRadiusTiles,
            durationMs: ashDurationMs,
            speedMultiplier: 0.8,
            sourceSkillId: 'cultivate_aftermath',
          });
        }
      : undefined;

    const position = this.deps.getPlayerPosition();
    const playerEntityId = this.deps.getPlayerEntityId();

    const zoneConfig: CultivateZoneConfig = {
      id: `cultivate-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      type: 'fire_zone',
      position,
      shape: 'circle',
      radiusTiles: this.config.radiusTiles,
      durationMs: resolvedDuration,
      dotDefId: 'burning',
      dotIntensity: this.config.dotIntensity,
      tickIntervalMs: this.config.tickIntervalMs,
      sourceSkillId: 'cultivate',
      friendlyEntityId: playerEntityId,
      ...(spreadActive && {
        expandsOverTime: true,
        expansionRateMs,
        expansionTiles,
        maxRadiusTiles,
      }),
      ...(onExpiry && { onExpiry }),
    };

    const zoneId = this.deps.zones.create(zoneConfig);
    this._activeZoneIds.push(zoneId);

    // Directional shove (Spread upgrade)
    if (spreadActive) {
      const shove = this.deps.getShoveDirection();
      if (shove) {
        this.deps.zones.moveZone(zoneId, shove.dx, shove.dy, 1);
      }
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._activeZoneIds.length = 0;
  }
}
